import { ReactNode, useId } from 'react';

export type Program = 'destaque' | 'engajamento' | 'ecoa' | 'reconhecimento';

/** Cor de fundo de cada programa. Um painel nunca mistura a cor de dois programas. */
export const PROGRAM_BACKGROUND: Record<Program, string> = {
  destaque: 'bg-brand-green',
  engajamento: 'bg-brand-green-light',
  ecoa: 'bg-brand-purple-light',
  reconhecimento: 'bg-brand-pink-light',
};

const PANEL_BODY = 'py-6 px-8 text-content';

interface BaseProps {
  /** Título renderizado acima do bloco colorido. Com ele, o conjunto vira uma `<section>` rotulada. */
  title?: ReactNode;
  /** Nível do heading do título. Padrão: 2. */
  headingLevel?: 2 | 3 | 4;
  children: ReactNode;
}

type ProgramPanelProps = BaseProps &
  (
    | { program: Exclude<Program, 'reconhecimento'>; aside?: never }
    | {
        program: 'reconhecimento';
        /** Coluna creme ao lado do bloco rosa (320px a partir do `md`, abaixo no mobile). */
        aside?: ReactNode;
      }
  );

/**
 * Bloco colorido de um programa (destaque, engajamento, ECOA ou reconhecimento).
 *
 * A cor vem só de `program`; não há prop para sobrescrever o fundo.
 *
 * Contraste: o texto base é `text-content`. Texto secundário dentro do painel
 * também usa `text-content`, e não `text-content-soft`, que reprova o AA sobre
 * menta, lilás e rosa. Para destaque de cor, use `text-brand-blue-dark`.
 */
export default function ProgramPanel(props: ProgramPanelProps) {
  const { program, title, headingLevel = 2, children } = props;
  const titleId = useId();
  const aside = props.program === 'reconhecimento' ? props.aside : undefined;
  const background = PROGRAM_BACKGROUND[program];

  const panel = aside ? (
    <div className="grid overflow-hidden rounded-jj-panel md:grid-cols-[1fr_320px]">
      <div className={`${background} ${PANEL_BODY}`}>{children}</div>
      <div className="bg-brand-yellow-light p-6 text-content">{aside}</div>
    </div>
  ) : (
    <div className={`${background} rounded-jj-panel ${PANEL_BODY}`}>{children}</div>
  );

  if (!title) return panel;

  const Heading = `h${headingLevel}` as const;

  return (
    <section aria-labelledby={titleId} className="flex flex-col gap-3">
      <Heading id={titleId} className="font-display font-bold text-[19px] text-brand-blue-dark">
        {title}
      </Heading>
      {panel}
    </section>
  );
}
